import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { PluginStore } from "graylog-web-plugin/plugin";
import { useQueryClient } from "@tanstack/react-query";
import { createInput as createInputRequest } from "hooks/useInputs";
import { fetchInputType } from "hooks/useInputType";
import Store from "logic/local-storage/Store";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { Col, Row, Button } from "components/bootstrap";
import { Select } from "components/common";
import { InputForm } from "components/inputs";
import useInputTypes from "components/inputs/useInputTypes";
import { KEY_PREFIX } from "hooks/usePaginatedInputs";
import useFeature from "hooks/useFeature";
import useInput from "hooks/useInput";
import { INPUT_SETUP_MODE_FEATURE_FLAG, InputSetupWizard } from "components/inputs/InputSetupWizard";
const StyledForm = styled.form`
  display: flex;
  gap: 0.25em;
`;
const FormGroup = styled.div`
  width: 300px;
  display: inline-block;
  margin-bottom: 0;
  vertical-align: middle;
`;
export const SETUP_WIZARD_INPUT_ID_KEY = "setup_wizard_input_id";
const CreateInputControl = () => {
  var _a;
  const { t } = useTranslation("inputs");
  const [showConfigurationForm, setShowConfigurationForm] = useState(false);
  const [selectedInput, setSelectedInput] = useState(void 0);
  const [selectedInputDefinition, setSelectedInputDefinition] = useState(void 0);
  const [customInputConfiguration, setCustomInputConfiguration] = useState(void 0);
  const [showWizard, setShowWizard] = useState(false);
  const [createdInputId, setCreatedInputId] = useState(null);
  const [createdInputData, setCreatedInputData] = useState(null);
  const [pendingWizardInputId, setPendingWizardInputId] = useState(
    () => {
      var _a2;
      return (_a2 = Store.sessionGet(SETUP_WIZARD_INPUT_ID_KEY)) != null ? _a2 : null;
    }
  );
  const sendTelemetry = useSendTelemetry();
  const inputTypes = useInputTypes();
  const queryClient = useQueryClient();
  const inputSetupFeatureFlagIsEnabled = useFeature(INPUT_SETUP_MODE_FEATURE_FLAG);
  const { data: pendingWizardInputData } = useInput(pendingWizardInputId);
  const hasPendingStorageWizard = inputSetupFeatureFlagIsEnabled && !!pendingWizardInputId;
  const showWizardFromStorage = hasPendingStorageWizard && !!pendingWizardInputData && !showWizard;
  const wizardInputFromStorage = pendingWizardInputData ? pendingWizardInputData : null;
  useEffect(() => {
    if (showWizardFromStorage) {
      Store.sessionDelete(SETUP_WIZARD_INPUT_ID_KEY);
    }
  }, [showWizardFromStorage]);
  const openWizard = (inputId, inputData) => {
    setCreatedInputId(inputId);
    setCreatedInputData(inputData);
    setShowWizard(true);
  };
  const closeLocalWizard = () => {
    setShowWizard(false);
    setCreatedInputId(null);
    setCreatedInputData(null);
  };
  const closeStorageWizard = () => {
    Store.sessionDelete(SETUP_WIZARD_INPUT_ID_KEY);
    setPendingWizardInputId(null);
  };
  const closeWizard = () => {
    if (showWizard) {
      closeLocalWizard();
      return;
    }
    closeStorageWizard();
  };
  const resetFields = () => {
    setSelectedInput(void 0);
    setSelectedInputDefinition(void 0);
  };
  const formatSelectOptions = () => {
    let options = [];
    if (inputTypes) {
      const inputTypesIds = Object.keys(inputTypes);
      options = inputTypesIds.map((id) => ({ value: id, label: inputTypes[id] }));
      options.sort(
        (inputTypeA, inputTypeB) => inputTypeA.label.toLowerCase().localeCompare(inputTypeB.label.toLowerCase())
      );
    } else {
      options.push({ value: "none", label: t("create_input.no_inputs_available"), disabled: true });
    }
    return options;
  };
  const onInputSelect = (selected) => {
    if (selected === "") {
      resetFields();
    }
    setSelectedInput(selected);
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_SELECTED, {
      app_action_value: "input-select",
      event_details: { value: selected }
    });
    fetchInputType(selected).then((inputDefinition) => setSelectedInputDefinition(inputDefinition));
  };
  const onCustomInputClose = () => {
    setCustomInputConfiguration(void 0);
  };
  const handleInputTypeSubmit = (event) => {
    event.preventDefault();
    if (hasPendingStorageWizard) {
      return;
    }
    const customConfiguration = PluginStore.exports("inputConfiguration").find(
      (inputConfig) => inputConfig.type === selectedInput
    );
    setCustomInputConfiguration(customConfiguration);
    setShowConfigurationForm(true);
  };
  const createInput = (data) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.INPUTS.INPUT_CREATED, {
      app_action_value: "input-create"
    });
    createInputRequest(data).then((response) => {
      queryClient.invalidateQueries({ queryKey: KEY_PREFIX });
      if (inputSetupFeatureFlagIsEnabled && (response == null ? void 0 : response.id)) {
        setTimeout(() => openWizard(response.id, data), 500);
      }
      resetFields();
    });
  };
  const createInputForWizard = () => {
    if (!createdInputId || !createdInputData) return null;
    return {
      id: createdInputId,
      title: createdInputData.title,
      type: createdInputData.type,
      attributes: createdInputData.configuration,
      global: createdInputData.global || false,
      node: createdInputData.node || null
    };
  };
  const CustomInputsConfiguration = customInputConfiguration ? customInputConfiguration.component : null;
  return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(StyledForm, { className: "form-inline", onSubmit: handleInputTypeSubmit }, /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Select,
    {
      placeholder: t("create_input.select_input"),
      options: formatSelectOptions(),
      onChange: onInputSelect,
      value: selectedInput
    }
  )), "\xA0", /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", type: "submit", disabled: !selectedInput || hasPendingStorageWizard }, t("create_input.launch_new"))), selectedInputDefinition && (customInputConfiguration ? /* @__PURE__ */ React.createElement(CustomInputsConfiguration, { onClose: onCustomInputClose }) : showConfigurationForm && /* @__PURE__ */ React.createElement(
    InputForm,
    {
      key: "configuration-form-input",
      setShowModal: setShowConfigurationForm,
      configFields: selectedInputDefinition.requested_configuration,
      description: selectedInputDefinition.description,
      title: /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "create_input.launch_new_type",
          values: { type: (_a = inputTypes[selectedInput]) != null ? _a : "" },
          components: { type: /* @__PURE__ */ React.createElement("em", null) }
        }
      )),
      submitButtonText: t("create_input.launch"),
      typeName: selectedInput,
      handleSubmit: createInput
    }
  )), inputSetupFeatureFlagIsEnabled && (showWizard || showWizardFromStorage) && /* @__PURE__ */ React.createElement(
    InputSetupWizard,
    {
      input: showWizard ? createInputForWizard() : wizardInputFromStorage,
      show: true,
      onClose: closeWizard
    }
  )));
};
export default CreateInputControl;
